import { QueryLazyOptions } from "@apollo/client";
import ClearIcon from "@assets/images/clear2.svg";
import InfoIcon from "@assets/images/info.svg";
import { Box, Chip, Paper, Tooltip } from "@material-ui/core";
import { Theme, withStyles } from "@material-ui/core/styles";
import { useUser } from "@saleor/auth";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import { DistributionChannelType, Exact } from "@saleor/graphql";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";

import useStyles from "./styles";

interface IProps {
  keyFilterApi?: string;
  handleSelectedSearch?: (value: string[]) => void;
  inputValue?: string;
  setInputValue?: React.Dispatch<React.SetStateAction<string>>;
  placeholder?: string;
  first?: number;
  reRender?: boolean;
  headerText?: string;
  message?: string;
  contained?: boolean;
  name: string;
  maxCount?: number;
  getDataSuggestions: (
    options?: QueryLazyOptions<
      Exact<{
        filter?: any;
        first?: number;
        distributionChannelType?: DistributionChannelType;
        includeOneTimeCustomer?: boolean;
      }>
    >
  ) => void;
  fetchDataSuggestions: any;
  loadingDataSuggestion: boolean;
  dataOptions: any[];
  error?: boolean;
  getByValue?: boolean;
  isShowSelected?: boolean;
  emptyBoxClasses?: any;
  suggestBoxClasses?: any;
  isCheckingRole?: boolean;
  isIncludeOneTimeCustomer?: boolean;
  id?: string;
}

interface ChipData {
  key: string;
  label: string;
  value: string;
}

const CustomTooltip = withStyles(
  (theme: Theme) => ({
    tooltip: {
      backgroundColor: theme.palette.grey[600],
      color: "#FFFFFF",
      fontSize: "1.2rem",
      maxWidth: "none"
    }
  }),
  { name: "index" }
)(Tooltip);

const AutoSuggestionMutipleSelect = (props: IProps) => {
  const {
    handleSelectedSearch,
    placeholder,
    first,
    headerText,
    reRender,
    message,
    contained,
    maxCount = 10,
    getDataSuggestions,
    loadingDataSuggestion,
    keyFilterApi,
    dataOptions,
    error,
    getByValue,
    isShowSelected,
    emptyBoxClasses,
    suggestBoxClasses,
    isCheckingRole,
    isIncludeOneTimeCustomer,
    id
  } = props;

  const classes = useStyles();
  const [value, setValue] = useState<string>("");
  const [isReset, setIsReset] = useState<boolean>(false);
  const [arr, setArr] = useState<ChipData[]>([]);
  const { zone } = useUser();

  const handleChange = event => {
    setValue(event);
  };

  const handleOnSelectedSearch = value => {
    if (!value || arr.find(item => item.key === value)) {
      setIsReset(prevState => !prevState);
      return;
    }

    let valueToArr = value;
    if (getByValue) {
      valueToArr =
        dataOptions.find(item => item.title === value)?.value || null;
      if (!valueToArr) {
        return;
      }
    } else {
      valueToArr =
        dataOptions.find(item => item.title === value)?.title || null;
      if (!valueToArr) {
        return;
      }
    }
    const arrClone = [...arr];
    arrClone.push({
      key: value,
      label: value,
      value: valueToArr?.toString()
    });
    setIsReset(prevState => !prevState);
    setArr(arrClone);
    handleSelectedSearch(
      arrClone.map(item => (getByValue ? item.value : item.label))
    );
  };

  const handleDelete = (chipToDelete: ChipData) => () => {
    const newArr = arr.filter(chip => chip.key !== chipToDelete.key);
    setArr(newArr);
    handleSelectedSearch(
      newArr.map(item => (getByValue ? item.value : item.label))
    );
  };

  useEffect(() => {
    if (value.length >= 2) {
      const filter = {
        [keyFilterApi]: value
      };

      getDataSuggestions({
        variables: {
          first: first || 100,
          filter,
          includeOneTimeCustomer: isIncludeOneTimeCustomer,
          distributionChannelType: isCheckingRole
            ? (zone?.toUpperCase() as DistributionChannelType)
            : undefined
        }
      });
    }
  }, [value]);

  useEffect(() => {
    setValue("");
    setArr([]);
    setIsReset(prevState => !prevState);
  }, [reRender]);

  const renderEmptyBox = () => (
    <>
      <div className={classNames(classes.boxList, emptyBoxClasses)}>
        <MyTypography className={classes.headerTxt} variant="body3">
          {headerText} {isShowSelected && "(0)"}
        </MyTypography>
        <Box className={classes.info}>
          <SVG width={20} src={InfoIcon} className={classes.icon} />
          <MyTypography variant="caption" className={classes.caption}>
            {message}
          </MyTypography>
        </Box>
      </div>
    </>
  );

  return (
    <>
      <Autocomplete
        resetAlways
        contained={contained}
        optionsProp={dataOptions}
        placeholderProps={placeholder}
        onChangeProps={handleChange}
        loading={loadingDataSuggestion}
        onSearch={handleOnSelectedSearch}
        isReset={isReset}
        error={error}
        disabled={arr.length >= maxCount}
        notUseFilterOptions
        id={id}
        testId={id}
      />
      {arr.length > 0 ? (
        <div className={classNames(classes.chipBox, suggestBoxClasses)}>
          <MyTypography className={classes.chipText} variant={"body3"}>
            {headerText} {isShowSelected && `(${arr.length || 0})`}
          </MyTypography>
          <Paper component="ul" className={classes.chipPaper}>
            {arr.map(data => (
              <li key={data.key} className={classes.chipLi}>
                <CustomTooltip title={data.label} placement="top">
                  <Chip
                    deleteIcon={
                      <img
                        className={classes.deleteIcon}
                        src={ClearIcon}
                        alt="delete"
                      />
                    }
                    label={data.label}
                    onDelete={handleDelete(data)}
                    className={classes.chip}
                  />
                </CustomTooltip>
              </li>
            ))}
          </Paper>
        </div>
      ) : (
        renderEmptyBox()
      )}
    </>
  );
};

export default AutoSuggestionMutipleSelect;
